import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { IResourceItemType } from '@/api/types';

import { ButtonBase, TableCard } from '@/components/common';
import { getDescription, getLabel, isResourceAnnouncementItem, isResourceLinkItem } from '@/helpers';
import { VisibilityChip } from '.';
import { useTimezone } from '@/hooks';
import { MONTH_DAY_YEAR_HOUR_FORMAT_NBSP } from '@/config/variables';
import { otherColors } from '@/styles/colors';
import { DeleteBlue, PenWhite } from '@/assets/icons';

interface IProps {
  items: IResourceItemType[];
  openEditModal: (item: IResourceItemType) => void;
  openDeleteModal: (item: IResourceItemType) => void;
}

const ResourceItemsTableMobile = ({ items, openEditModal, openDeleteModal }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();

  return (
    <TableCard>
      {items.map((item, id) => (
        <TableCard.CardTableRowsWrapper
          key={id}
          isCollapsable={true}
          firstBgColor="#f3f4f6"
          secondBgColor="#fff"
          activeBorderColor="#0087dd"
        >
          <TableCard.TableRow firstBgColor="#f3f4f6" secondBgColor="#fff">
            <TableCard.Cell width="60%">
              <TableCard.CellHeader>{t`resources.foldersList.headerName`}</TableCard.CellHeader>
              <TableCard.CellValue>
                {isResourceLinkItem(item) ? <LinkName href={item.url}>{getLabel(item)}</LinkName> : getLabel(item)}
              </TableCard.CellValue>
            </TableCard.Cell>
            <TableCard.Cell width="40%">
              <TableCard.CellHeader>{t`resources.foldersList.headerVisibility`}</TableCard.CellHeader>
              <TableCard.CellValue>
                <VisibilityChip visibility={item.publicationStatus} />
              </TableCard.CellValue>
            </TableCard.Cell>
          </TableCard.TableRow>
          <TableCard.TableExpandedRow>
            <TableCard.ExpandedRowContainer>
              <TableCard.Cell width="50%">
                <TableCard.CellHeader>{t`resources.foldersList.headerPostedDate`}</TableCard.CellHeader>
                <TableCard.CellValue>
                  {item.dateStart && formatDateTime(item.dateStart, MONTH_DAY_YEAR_HOUR_FORMAT_NBSP)}
                </TableCard.CellValue>
              </TableCard.Cell>
              <TableCard.Cell width="50%">
                <TableCard.CellHeader>{t`resources.foldersList.headerExpiration`}</TableCard.CellHeader>
                <TableCard.CellValue>
                  {item.dateEnd && formatDateTime(item.dateEnd, MONTH_DAY_YEAR_HOUR_FORMAT_NBSP)}
                </TableCard.CellValue>
              </TableCard.Cell>
            </TableCard.ExpandedRowContainer>
            <TableCard.ExpandedRowContainer>
              <TableCard.Cell width="50%">
                <TableCard.CellHeader>{t`resources.foldersList.headerCreatedBy`}</TableCard.CellHeader>
                <TableCard.CellValue>{item.createdByName?.replaceAll('.', ' ') || ''}</TableCard.CellValue>
              </TableCard.Cell>
              <TableCard.Cell width="50%">
                <TableCard.CellHeader>{t`resources.foldersList.headerModifiedBy`}</TableCard.CellHeader>
                <TableCard.CellValue>{item.modifiedByName?.replaceAll('.', ' ') || ''}</TableCard.CellValue>
              </TableCard.Cell>
            </TableCard.ExpandedRowContainer>
            {!isResourceAnnouncementItem(item) && (
              <TableCard.ExpandedRowContainer>
                <TableCard.Cell width="100%">
                  <TableCard.CellHeader>{t`resources.foldersList.headerDescription`}</TableCard.CellHeader>
                  <TableCard.CellValue>{getDescription(item)}</TableCard.CellValue>
                </TableCard.Cell>
              </TableCard.ExpandedRowContainer>
            )}
            <TableCard.ExpandedRowContainer>
              <TableCard.Cell width="100%">
                <ButtonContainer>
                  <EditButton onClick={() => openEditModal(item)} icon={PenWhite}>
                    {t`common.btnEdit`}
                  </EditButton>
                  <DeleteButton onClick={() => openDeleteModal(item)} icon={DeleteBlue}>
                    {t`common.btnDelete`}
                  </DeleteButton>
                </ButtonContainer>
              </TableCard.Cell>
            </TableCard.ExpandedRowContainer>
          </TableCard.TableExpandedRow>
        </TableCard.CardTableRowsWrapper>
      ))}
    </TableCard>
  );
};

export { ResourceItemsTableMobile };

const LinkName = styled.a`
  color: ${otherColors.lxc.blue};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;

  button {
    flex: 1;
  }
`;

const EditButton = styled(ButtonBase)`
  background-color: ${otherColors.lxc.blue} !important;
  color: #fff !important;
  height: 30px;
  border: none;
  padding: 0 12px;
`;

const DeleteButton = styled(ButtonBase)`
  background-color: #fff;
  color: ${otherColors.lxc.blue};
  height: 30px;
  border: 1px solid ${otherColors.lxc.blue};
  padding: 0 12px;
`;
